import { Injectable, inject } from '@angular/core';
import { Observable, Subject, BehaviorSubject, throwError } from 'rxjs';
import { io, Socket } from 'socket.io-client';
import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';

export interface RealtimeEvent {
  type: string;
  payload: any;
  timestamp: Date;
  service?: string;
}

@Injectable({ providedIn: 'root' })
export class RealtimeService {
  private readonly authService = inject(AuthService);
  private socket: Socket | null = null;
  private readonly connected$ = new BehaviorSubject<boolean>(false);
  private readonly events$ = new Subject<RealtimeEvent>();

  readonly connected = this.connected$.asObservable();
  readonly events = this.events$.asObservable();

  /**
   * Connect to WebSocket server
   */
  connect(): void {
    console.log('🌐 [DEBUG] RealtimeService.connect() called');
    
    if (this.socket?.connected) {
      console.log('✅ [DEBUG] Socket already connected, skipping');
      return;
    }

    const token = this.authService.token();
    const user = this.authService.user();
    
    console.log('🔑 [DEBUG] Token exists:', !!token);
    console.log('👤 [DEBUG] User:', user);

    if (!token) {
      console.warn('❌ [DEBUG] Cannot connect to WebSocket: No authentication token');
      return;
    }

    // Connect to unified WebSocket gateway
    const wsUrl = environment.websocketUrl || `${window.location.origin.replace(/^http/, 'ws')}/api/v1/stream`;
    console.log('🔗 [DEBUG] WebSocket URL:', wsUrl);
    
    this.socket = io(wsUrl, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      query: {
        userId: (user as any)?.id || (user as any)?.sub || (user as any)?.userId,
        tenantId: user?.tenantId,
      },
      auth: {
        token,
        tenantId: user?.tenantId,
      },
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
    });
    
    console.log('🚀 [DEBUG] Socket.IO client created');

    this.socket.on('connect', () => {
      console.log('✅ [DEBUG] Unified WebSocket connected:', this.socket?.id);
      this.connected$.next(true);
      
      // Subscribe to all services
      this.socket?.emit('subscribe', { service: 'all' });
      console.log('📡 [DEBUG] Subscribed to all services');
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ [DEBUG] WebSocket disconnected:', reason);
      this.connected$.next(false);
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ [DEBUG] WebSocket connection error:', error);
      this.connected$.next(false);
    });

    // Listen to all events
    this.socket.onAny((eventName, ...args) => {
      console.log('📡 [DEBUG] Socket event received:', { eventName, args, timestamp: new Date().toISOString() });
      this.events$.next({
        type: eventName,
        payload: args[0] || {},
        timestamp: new Date(),
      });
    });

    // Service-specific events
    this.setupServiceListeners();
    console.log('🔧 [DEBUG] Service listeners set up');
  }

  /**
   * Setup service-specific event listeners for all services
   */
  private setupServiceListeners(): void {
    if (!this.socket) return;

    // Task Service Events
    this.socket.on('tasks:taskCreated', (data) => this.emitEvent('tasks:taskCreated', data, 'task-service'));
    this.socket.on('tasks:taskUpdated', (data) => this.emitEvent('tasks:taskUpdated', data, 'task-service'));
    this.socket.on('tasks:taskStatusChanged', (data) => this.emitEvent('tasks:taskStatusChanged', data, 'task-service'));
    this.socket.on('tasks:taskAssigned', (data) => this.emitEvent('tasks:taskAssigned', data, 'task-service'));
    this.socket.on('tasks:commentAdded', (data) => this.emitEvent('tasks:commentAdded', data, 'task-service'));

    // Project Service Events
    this.socket.on('projects:projectCreated', (data) => this.emitEvent('projects:projectCreated', data, 'project-service'));
    this.socket.on('projects:projectUpdated', (data) => this.emitEvent('projects:projectUpdated', data, 'project-service'));
    this.socket.on('projects:projectStatusChanged', (data) => this.emitEvent('projects:projectStatusChanged', data, 'project-service'));
    this.socket.on('projects:departmentCreated', (data) => this.emitEvent('projects:departmentCreated', data, 'project-service'));
    this.socket.on('projects:departmentUpdated', (data) => this.emitEvent('projects:departmentUpdated', data, 'project-service'));

    // User Service Events
    this.socket.on('users:user:created', (data) => this.emitEvent('users:user:created', data, 'user-service'));
    this.socket.on('users:user:updated', (data) => this.emitEvent('users:user:updated', data, 'user-service'));
    this.socket.on('users:user:deleted', (data) => this.emitEvent('users:user:deleted', data, 'user-service'));
    this.socket.on('users:role:created', (data) => this.emitEvent('users:role:created', data, 'user-service'));
    this.socket.on('users:role:updated', (data) => this.emitEvent('users:role:updated', data, 'user-service'));
    this.socket.on('users:team:created', (data) => this.emitEvent('users:team:created', data, 'user-service'));

    // Auth Service Events
    this.socket.on('auth:loggedIn', (data) => this.emitEvent('auth:loggedIn', data, 'auth-service'));
    this.socket.on('auth:loggedOut', (data) => this.emitEvent('auth:loggedOut', data, 'auth-service'));
    this.socket.on('auth:sessionExpired', (data) => this.emitEvent('auth:sessionExpired', data, 'auth-service'));
    this.socket.on('auth:permissionChanged', (data) => this.emitEvent('auth:permissionChanged', data, 'auth-service'));

    // Notifications Service Events
    this.socket.on('notifications:notification', (data) => this.emitEvent('notifications:notification', data, 'notifications-service'));
    this.socket.on('notifications:unread_count', (data) => this.emitEvent('notifications:unread_count', data, 'notifications-service'));

    // Attendance Service Events
    this.socket.on('attendances:attendanceMarkedIn', (data) => this.emitEvent('attendances:attendanceMarkedIn', data, 'attendance-service'));
    this.socket.on('attendances:attendanceMarkedOut', (data) => this.emitEvent('attendances:attendanceMarkedOut', data, 'attendance-service'));
    this.socket.on('attendances:attendanceUpdated', (data) => this.emitEvent('attendances:attendanceUpdated', data, 'attendance-service'));
    this.socket.on('attendances:shiftCreated', (data) => this.emitEvent('attendances:shiftCreated', data, 'attendance-service'));
    this.socket.on('attendances:overtimeCreated', (data) => this.emitEvent('attendances:overtimeCreated', data, 'attendance-service'));

    // Customer Service Events
    this.socket.on('customers:customer:created', (data) => this.emitEvent('customers:customer:created', data, 'customer-service'));
    this.socket.on('customers:customer:updated', (data) => this.emitEvent('customers:customer:updated', data, 'customer-service'));
    this.socket.on('customers:customer:stageChanged', (data) => this.emitEvent('customers:customer:stageChanged', data, 'customer-service'));

    // Academic Service Events
    this.socket.on('academic:programCreated', (data) => this.emitEvent('academic:programCreated', data, 'academic-service'));
    this.socket.on('academic:applicationCreated', (data) => this.emitEvent('academic:applicationCreated', data, 'academic-service'));
    this.socket.on('academic:applicationStatusChanged', (data) => this.emitEvent('academic:applicationStatusChanged', data, 'academic-service'));

    // Reporting Service Events
    this.socket.on('reporting:reportGenerated', (data) => this.emitEvent('reporting:reportGenerated', data, 'reporting-service'));
    this.socket.on('reporting:analyticsUpdated', (data) => this.emitEvent('reporting:analyticsUpdated', data, 'reporting-service'));

    // Audit Service Events
    this.socket.on('audit:logCreated', (data) => this.emitEvent('audit:logCreated', data, 'audit-service'));
    this.socket.on('audit:securityAlert', (data) => this.emitEvent('audit:securityAlert', data, 'audit-service'));

    // Automation Service Events
    this.socket.on('automation:workflowExecuted', (data) => this.emitEvent('automation:workflowExecuted', data, 'automation-service'));
    this.socket.on('automation:workflowTriggered', (data) => this.emitEvent('automation:workflowTriggered', data, 'automation-service'));

    // Platform Service Events
    this.socket.on('platform:tenantCreated', (data) => this.emitEvent('platform:tenantCreated', data, 'platform-service'));
    this.socket.on('platform:tenantUpdated', (data) => this.emitEvent('platform:tenantUpdated', data, 'platform-service'));
    this.socket.on('platform:systemStatusChanged', (data) => this.emitEvent('platform:systemStatusChanged', data, 'platform-service'));
  }

  private emitEvent(type: string, payload: any, service?: string): void {
    this.events$.next({
      type,
      payload,
      timestamp: new Date(),
      service,
    });
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket = null;
      this.connected$.next(false);
    }
  }

  /**
   * Subscribe to specific event type
   */
  on<T = any>(eventName: string): Observable<T> {
    return new Observable<T>((observer) => {
      if (!this.socket) {
        observer.error(new Error('Socket not connected'));
        return;
      }

      const handler = (data: T) => {
        observer.next(data);
      };

      this.socket.on(eventName, handler);

      return () => {
        this.socket?.off(eventName, handler);
      };
    });
  }

  /**
   * Emit event to server
   */
  emit(eventName: string, data?: any): void {
    if (!this.socket?.connected) {
      console.warn(`Cannot emit ${eventName}: Socket not connected`);
      return;
    }

    this.socket.emit(eventName, data);
  }

  /**
   * Subscribe to a service or event
   */
  subscribe(service?: string, event?: string): void {
    this.emit('subscribe', { service, event });
  }

  /**
   * Unsubscribe from a service or event
   */
  unsubscribe(service?: string, event?: string): void {
    this.emit('unsubscribe', { service, event });
  }

  /**
   * Join a room/channel (deprecated, use subscribe)
   */
  join(room: string): void {
    this.emit('subscribe', { event: room });
  }

  /**
   * Leave a room/channel (deprecated, use unsubscribe)
   */
  leave(room: string): void {
    this.emit('unsubscribe', { event: room });
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.socket?.connected ?? false;
  }

  /**
   * Reconnect manually
   */
  reconnect(): void {
    this.disconnect();
    this.connect();
  }
}

